import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import { successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'

import { AIR_ROOT_KEY } from '../query-keys'
import { getAutomationsList } from './api'

/**
 * Query keys for Automation Settings.
 *
 * Tuples are byte-identical to the pre-split `airKeys.automation*` — the split
 * moved where they are declared, never what they produce.
 */
export const automationKeys = {
	all: [...AIR_ROOT_KEY, 'automations'] as const,
	automations_list: (tenant: string) =>
		[...automationKeys.all, tenant] as const,
	automation_details: (tenant: string, mapping_id?: string) =>
		[...automationKeys.all, tenant, mapping_id] as const,
}

/**
 * Query options for Automation Settings
 */
export const automationQueries = {
	automations_list: (tenant: string) =>
		queryOptions({
			queryKey: automationKeys.automations_list(tenant),
			queryFn: ({ signal }) => getAutomationsList(tenant, signal),
			staleTime: ({ state }) => successStaleTime(state, 2, 'm'),
			gcTime: time(3, 'm'),
		}),
	automation_details: (tenant: string, mapping_id?: string) =>
		queryOptions({
			queryKey: automationKeys.automation_details(tenant, mapping_id),
			queryFn: ({ signal }) => getAutomationsList(tenant, signal, mapping_id),
			gcTime: time(3, 'm'),
			staleTime: ({ state }) => successStaleTime(state, 2, 'm'),
			select: (data) => {
				if (!mapping_id) return undefined

				const automation = data.responses.find(
					(response) => response.mapping_id === decodeURIComponent(mapping_id),
				)

				return automation
			},
			enabled: !!mapping_id,
		}),
}

/**
 * Use automations list
 */
export const useAutomationsList = () => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(automationQueries.automations_list(tenant))
}

/**
 * Use automation details
 * @param mapping_id - The mapping ID
 */
export const useAutomationDetails = (mapping_id?: string) => {
	const tenant = useCurrentTenant()

	return useSuspenseQuery(
		automationQueries.automation_details(tenant, mapping_id),
	)
}
